import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Observable, of } from 'rxjs';
import { NavigationDropdown, NavigationItem, NavigationSubheading } from 'src/@vex/interfaces/navigation-item.interface';
import { map } from 'rxjs/operators';
import { PermissionGroups } from '../utils/PermissionGroups';
import { LawFirmListText, LegalNetworkGroups, LegalNetworkMenu, LegalNetworkRoutes, PracticeAreaListText } from '../utils/legal-network-constants';
import { P } from '@angular/cdk/keycodes';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  permissions: Array<string>;

  constructor(private _msalService: MsalService) { }

  //Menu Permission - Change menu based on user permissions here
  mountMenu(): Observable<NavigationItem[]> {
    return this.getGroups().pipe(
      map((permissions) => {
        this.permissions = permissions;
        
        /*Test the menu here
        this.permissions= new Array<string>();
        this.permissions.push("VPN-Users");
        this.permissions.push(PermissionGroups.ClientServicesContributor);
        */
        
        const fullMenu = this.getMenu();
        return this.filterMenuByPermissions(fullMenu);
      })
    );
  }
  private filterMenuByPermissions(menu: NavigationItem[]): NavigationItem[] {
    return menu
      .map((item) => {
        if (this.hasChildren(item)) {
          const filteredChildren = this.filterMenuByPermissions(item.children);
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item) => {
        if (this.hasChildren(item) && item.children.length === 0) {
          return false;
        }
        if ('groups' in item) {
          const g = item.groups.map(group => group.toString());
          return (
            !item.groups ||
            item.groups.length === 0 ||
            this.hasPermission(g)
          );
        }
        return true;
      });
  }
  
  private hasChildren(item: NavigationItem): item is NavigationDropdown | NavigationSubheading {
    return item.type === 'dropdown' || item.type === 'subheading';
  }
  

  // This method is called once and a list of permissions is stored in the permissions property
  getGroups(): Observable<string[]> {
    console.log("Getting groups");
    return new Observable<string[]>((observer) => {
      const checkAccounts = () => {
        let accounts = this._msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this._msalService.instance.setActiveAccount(accounts[0]);
          const groups = accounts[0].idTokenClaims.groups as string[];
          /* Test the groups here
          const groups = new Array<string>();
          groups.push(PermissionGroups.ClientServicesContributor);*/
          
          observer.next(groups);
          observer.complete();
          console.log("accounts found", groups);
        } else {
          // Retry after 1 second
          setTimeout(checkAccounts, 1000);
          console.log("No accounts found");
        }
      };
  
      // Start the account check
      checkAccounts();
    });
  }

  hasPermission(group: string[]): boolean {
    if (this.permissions && this.permissions.find(permission => {
      return group.some(item => permission.toUpperCase().trim() == item.toUpperCase().trim());
    })) {
      return true;
    } else {
      return false;
    }
  }


  private getMenu(): NavigationItem[] {
    return [
      {
        type: 'subheading',
        label: 'Modules',
        children: [
          {
            type: 'link',
            label: 'Dashboard',
            route: '/home',
            icon: 'mat:dashboard',
            routerLinkActiveOptions: { exact: true },
            groups: []
          },
          {
            type: 'link',
            label: 'My Alerts',
            route: '/notifications',
            icon: 'mat:notifications',
            routerLinkActiveOptions: { exact: true },
            groups: [
              PermissionGroups.Admin,
              PermissionGroups.BillingAdmin,
              PermissionGroups.BillingContributor,
              PermissionGroups.ClientServicesAdmin,
              PermissionGroups.ClientServicesContributor,
              PermissionGroups.EDIContributor
            ]
          },
          {
            type: 'dropdown',
            label: 'Clients',
            icon: 'mat:people',
            groups: [],
            children: [
              {
                type: 'link',
                label: 'Create',
                route: '/clients/create',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin]

              },
              {
                type: 'link',
                label: 'Search',
                route: '/clients/search',
                routerLinkActiveOptions: { exact: true },
                groups: []
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Members',
            icon: 'mat:card_membership',
            groups: [
              PermissionGroups.Admin,
              PermissionGroups.BillingAdmin,
              PermissionGroups.BillingContributor,
              PermissionGroups.MemberServicesAdmin,
              PermissionGroups.MemberServicesContributor,
              PermissionGroups.AccountsReceivableAdmin,
              PermissionGroups.EDIContributor
            ],
            children: [
              {
                type: 'link',
                label: 'Create',
                route: '/members/create',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin, PermissionGroups.BillingContributor, PermissionGroups.EDIContributor]
              },
              {
                type: 'link',
                label: 'Search',
                route: '/members/search',
                routerLinkActiveOptions: { exact: true },
                groups: [
                  PermissionGroups.Admin,
                  PermissionGroups.BillingAdmin,
                  PermissionGroups.BillingContributor,
                  PermissionGroups.MemberServicesAdmin,
                  PermissionGroups.MemberServicesContributor,
                  PermissionGroups.AccountsReceivableAdmin,
                  PermissionGroups.EDIContributor
                ]
              },
              {
                type: 'link',
                label: 'Assign Firms',
                route: '/members/assign-firms',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.MemberServicesAdmin, PermissionGroups.MemberServicesContributor, PermissionGroups.EDIContributor]
              },
              {
                type: 'link',
                label: 'IDP Changes',
                route: '/members/idp-pending-changes',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.MemberServicesAdmin, PermissionGroups.MemberServicesContributor, PermissionGroups.EDIContributor]
              },
              {
                type: 'link',
                label: 'Enrollment Duplicates',
                route: '/members/enrollment-duplicates',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin, PermissionGroups.EDIContributor]
              },
              {
                type: 'link',
                label: 'Cancellation Requests',
                route: '/members/cancellation-requests',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin,PermissionGroups.BillingAdmin]
              },
              {
                type: 'link',
                label: 'Iris Invoice Lookup',
                route: '/members/iris-invoice-lookup',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.AccountsReceivableAdmin, PermissionGroups.BillingAdmin, PermissionGroups.BillingContributor, PermissionGroups.EDIContributor]
              }
            ]
          },
          { 
            type: 'dropdown', 
            label: 'Finance', 
            icon: 'mat:monetization_on', 
            groups: [PermissionGroups.Admin, PermissionGroups.AccountsPayableAdmin, PermissionGroups.MemberServicesAdmin, PermissionGroups.AccountsReceivableAdmin],
            children: [
              { 
                type: 'dropdown',
                label: 'Accounts Receivable',
                groups: [PermissionGroups.Admin, PermissionGroups.AccountsPayableAdmin, PermissionGroups.AccountsReceivableAdmin],
                children: [
                  {
                    type: 'link',
                    label: 'Unpost Cash Receipts',
                    route: '/finance/accounts-receivable/unpost-cash-receipts',
                    routerLinkActiveOptions: { exact: true },
                    groups:[PermissionGroups.Admin, PermissionGroups.AccountsReceivableAdmin]
                  },
                ]

              },
              { 
                type: 'dropdown',
                label: 'Accounts Payable',
                groups: [PermissionGroups.Admin, PermissionGroups.AccountsPayableAdmin, PermissionGroups.AccountsPayableContributor],
                children: [
                  {
                    type: 'link',
                    label: 'Claims Payment',
                    route: '/finance/accounts-payable/claims-payment',
                    routerLinkActiveOptions: { exact: true },
                    groups: [PermissionGroups.Admin, PermissionGroups.AccountsPayableAdmin, PermissionGroups.AccountsPayableContributor]
                  },
                ]
              }
              ]
          },
          { 
            type: 'dropdown', 
            label: 'Marketing', 
            icon: 'mat:campaign', 
            groups: [PermissionGroups.Admin, PermissionGroups.MemberServicesAdmin, PermissionGroups.BillingAdmin, PermissionGroups.MarketingContributor],
            children: [
              {
                type: 'link',
                label: 'Landing Pages',
                route: '/marketing/landing-pages',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.MemberServicesAdmin, PermissionGroups.MemberServicesContributor, PermissionGroups.BillingAdmin, PermissionGroups.MarketingContributor]
              },
              {
                type: 'link',
                label: 'Email Templates',
                route: '/marketing/email-templates',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin, PermissionGroups.MemberServicesAdmin, PermissionGroups.MarketingContributor]
              },
              {
                type: 'link',
                label: 'Files',
                route: '/marketing/files',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin, PermissionGroups.MemberServicesAdmin, PermissionGroups.MarketingContributor]
              },
            ]
          },
          {
            type: 'dropdown',
            label: 'Cases',
            icon: 'mat:phone',
            groups: [PermissionGroups.Admin,PermissionGroups.MemberServicesAdmin,PermissionGroups.MemberServicesContributor],
            children: [
              {
                type: 'link',
                label: 'Search',
                route: '/cases',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin,PermissionGroups.MemberServicesAdmin,PermissionGroups.MemberServicesContributor]
              },
              {
                type: 'link',
                label: 'Referral Report',
                route: '/cases/referral-report',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin,PermissionGroups.MemberServicesAdmin]
              },
              {
                type: 'link',
                label: 'Dashboard',
                route: '/cases/dashboard',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin,PermissionGroups.MemberServicesAdmin],
              },
              {
                type: 'link',
                label: 'Questions',
                route: '/cases/settings/questions',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.MemberServicesAdmin]
              }
            ]

          },
          {
            type: 'dropdown',
            label: LegalNetworkMenu.label,
            icon: LegalNetworkMenu.icon,
            groups: LegalNetworkGroups,
            children: [
              {
                type: 'link',
                label: LawFirmListText.title,
                route: LegalNetworkRoutes.lawFirms,
                routerLinkActiveOptions: { exact: true },
                groups: LegalNetworkGroups
              },
              {
                type: 'link',
                label: PracticeAreaListText.title,
                route: LegalNetworkRoutes.practiceAreas,
                // Not exact, so the link stays highlighted on a practice area's detail page.
                routerLinkActiveOptions: { exact: false },
                groups: LegalNetworkGroups
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Products',
            icon: 'mat:inventory_2',
            groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin],
            children: [
              {
                type: 'link',
                label: 'Plan Attributes',
                route: '/products/plan-attributes',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin]
              },
              {
                type: 'link',
                label: 'Product Defaults',
                route: '/products/product',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin, PermissionGroups.BillingAdmin]
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Portal',
            icon: 'mat:web',
            groups: [PermissionGroups.Admin],
            children: [
              {
                type: 'link',
                label: 'Users',
                route: '/portal/users',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin]
              },
              {
                type: 'link',
                label: 'Permissions & Roles',
                route: '/portal/permissions',
                icon: 'mat:admin_panel_settings',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin]
              },
              {
                type: 'link',
                label: 'Feature Flags',
                route: '/portal/feature-flags',
                icon: 'mat:flag',
                routerLinkActiveOptions: { exact: true },
                groups: [PermissionGroups.Admin]
              }
            ]
          },
          {
            type: 'link',
            label: 'Staff',
            route: '/staff',
            icon: 'mat:badge',
            routerLinkActiveOptions: { exact: true },
            groups: [
              PermissionGroups.Admin,
              PermissionGroups.BillingAdmin
            ]
          },
          {
            type: 'dropdown',
            label: 'Logs',
            icon: 'mat:track_changes',
            groups: [PermissionGroups.Admin],
            children: [
              {
                type: 'link',
                label: 'Audit Logs',
                route: '/logs/audit',
                routerLinkActiveOptions: { exact: true },
                groups: []
              },
              {
                type: 'link',
                label: 'Error Logs',
                route: '/logs/error',
                routerLinkActiveOptions: { exact: true },
                groups: []
              }
            ]
          }
        ]
      }
    ];
  }
}
